import type {
  DocumentNode,
  FetchPolicy,
  MutationFetchPolicy,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { KEEP, RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client/core';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient as createWsClient } from 'graphql-ws';

import { useAuthUserStore } from '@/app/stores/user';
import { nativeApp } from '@/shared/services/native-app';

import { buildSentryAuthContext, SentryLevel, useSentry } from '@/shared/lib/sentry';
import session from '@/app/providers/session';

export enum EnumFetchPolicy {
  CACHE_FIRST = 'cache-first',
  NETWORK_ONLY = 'network-only',
  CACHE_ONLY = 'cache-only',
  NO_CACHE = 'no-cache',
}

export enum SERVICES {
  ACCESS_CONTROL_SERVICE = 'ob-access-control-service',
  ACCOUNT_SERVICE = 'ob-account-service',
  PAYMENT_SERVICE = 'ob-payment-service',
  CARD_SALARY_SERVICE = 'card-salary-service',
  RULE_ENGINE_SERVICE = 'ob-rule-engine-service',
  DOCUMENT_TEMPLATE_SERVICE = 'ob-document-template-service',
  IMPORT_PAYMENT_SERVICE = 'ob-import-payment-service',
  STATEMENT_SERVICE = 'ob-statement-service',
  NOTIFICATION_SERVICE = 'notification-service',
  LETTER_BANK_SERVICE = 'ob-letter-bank-service',
  FAQ_SERVICE = 'faq-service',
  INSTALLMENT_SERVICE = 'ob-installment-service',
  PAY_LOGIC_SERVICE = 'paylogic-service',
  OB_SALE_POINT = 'ob-sale-point',
  O24_INVOICES_SERVICE = 'o24-invoice-service',
}

const options = {
  cache: new InMemoryCache(),
  queryDeduplication: false,
  connectToDevTools: true,
  version: import.meta.env.VITE_APP_VERSION,
};

const buildFetchOptions = (signal?: AbortSignal) => ({
  credentials: 'include' as const,
  headers: session.applyDeviceIdHeaders() as unknown as HeadersInit,
  ...(signal ? { signal } : {}),
});

function createTransportLink(wsLink: ApolloLink, httpLink: ApolloLink) {
  return ApolloLink.from([
    new RemoveTypenameFromVariablesLink({
      except: {
        JSON: KEEP,
      },
    }),
    ApolloLink.split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      httpLink
    ),
  ]);
}

export const createClient = (serviceUrl: SERVICES) => {
  const httpUri = `${import.meta.env.VITE_APP_ENDPOINT}/mobile/${serviceUrl}/graphql`;
  const wsUri = httpUri
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://')
    .replace('/graphql', '/subscriptions');

  const errorLink = new ErrorLink((error) => {
    const { operation, result } = error as {
      operation: {
        variables?: Record<string, unknown>;
        operationName?: string;
        getContext: () => any;
        setContext: (ctx: any) => void;
      };
      result?: { errors?: unknown[] };
    };

    const operationVariables = { ...(operation?.variables ?? {}) };
    if (result?.errors?.length) {
      result?.errors.forEach((err: any) => {
        err.extensions = {
          ...(err.extensions ?? {}),
          operationVariables,
        };
      });
    }

    const { response } = operation.getContext();
    const headers = response?.headers;
    const errorClassification = headers?.get?.('x-error-classification');
    if (errorClassification === 'UnauthorizedException') nativeApp.sessionExpired();

    const shouldLogError = Boolean(result?.errors?.length);

    if (shouldLogError) {
      const authUserStore = useAuthUserStore();
      const graphQlFieldPath = (result?.errors?.[0] as { path?: Array<string | number> } | undefined)?.path?.[0];
      const graphQlFieldName = graphQlFieldPath ? String(graphQlFieldPath) : undefined;
      const payload = {
        service: serviceUrl,
        payload: operationVariables,
        error: {
          errors: result?.errors,
        },
        level: SentryLevel.ERROR,
        graphqlField: graphQlFieldName,
        typeAction: operation?.operationName,
        xTraceId: headers?.get?.('x-trace-id'),
        headerClassification: errorClassification ?? undefined,
        statusCode: response?.status,
        ...buildSentryAuthContext(authUserStore),
      };
      useSentry(payload);
    }
  });

  const httpLink = errorLink.concat(
    new HttpLink({
      uri: httpUri,
      includeExtensions: false,
      fetchOptions: buildFetchOptions(),
    })
  );

  const wsLink = new GraphQLWsLink(
    createWsClient({
      url: wsUri,
    })
  );

  const transportLink = createTransportLink(wsLink, httpLink);

  return new ApolloClient({
    link: transportLink,
    devtools: { enabled: serviceUrl === SERVICES.ACCOUNT_SERVICE },
    ...options,
  });
};

export const apolloClients = {
  [SERVICES.ACCESS_CONTROL_SERVICE]: createClient(SERVICES.ACCESS_CONTROL_SERVICE),
  [SERVICES.PAYMENT_SERVICE]: createClient(SERVICES.PAYMENT_SERVICE),
  [SERVICES.ACCOUNT_SERVICE]: createClient(SERVICES.ACCOUNT_SERVICE),
  [SERVICES.CARD_SALARY_SERVICE]: createClient(SERVICES.CARD_SALARY_SERVICE),
  [SERVICES.RULE_ENGINE_SERVICE]: createClient(SERVICES.RULE_ENGINE_SERVICE),
  [SERVICES.DOCUMENT_TEMPLATE_SERVICE]: createClient(SERVICES.DOCUMENT_TEMPLATE_SERVICE),
  [SERVICES.IMPORT_PAYMENT_SERVICE]: createClient(SERVICES.IMPORT_PAYMENT_SERVICE),
  [SERVICES.STATEMENT_SERVICE]: createClient(SERVICES.STATEMENT_SERVICE),
  [SERVICES.NOTIFICATION_SERVICE]: createClient(SERVICES.NOTIFICATION_SERVICE),
  [SERVICES.LETTER_BANK_SERVICE]: createClient(SERVICES.LETTER_BANK_SERVICE),
  [SERVICES.INSTALLMENT_SERVICE]: createClient(SERVICES.INSTALLMENT_SERVICE),
  [SERVICES.PAY_LOGIC_SERVICE]: createClient(SERVICES.PAY_LOGIC_SERVICE),
  [SERVICES.OB_SALE_POINT]: createClient(SERVICES.OB_SALE_POINT),
  [SERVICES.O24_INVOICES_SERVICE]: createClient(SERVICES.O24_INVOICES_SERVICE),
  [SERVICES.FAQ_SERVICE]: createClient(SERVICES.FAQ_SERVICE),
};

export function apolloQueryWithAbort<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  clientId: SERVICES,
  query: TypedDocumentNode<TData, TVariables>,

  options?: {
    variables?: TVariables;
    fetchPolicy?: FetchPolicy;
    abortController?: AbortController;
  }
) {
  const client = apolloClients[clientId];
  const queryOptions = {
    query,
    fetchPolicy: options?.fetchPolicy ?? EnumFetchPolicy.NETWORK_ONLY,
    context: {
      fetchOptions: {
        signal: options?.abortController?.signal,
      },
      headers: session.applyDeviceIdHeaders(),
    },
  } as unknown as ApolloClient.QueryOptions<TData, TVariables>;

  if (options?.variables) {
    Object.assign(queryOptions, { variables: options.variables });
  }

  return client.query<TData, TVariables>(queryOptions);
}

export function apolloMutation<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  clientId: SERVICES,
  mutation: TypedDocumentNode<TData, TVariables>,
  options?: {
    variables?: TVariables;
    fetchPolicy?: MutationFetchPolicy;
  }
) {
  const client = apolloClients[clientId];
  const mutationOptions = {
    mutation,
    fetchPolicy: options?.fetchPolicy ?? EnumFetchPolicy.NETWORK_ONLY,
    context: {
      headers: session.applyDeviceIdHeaders(),
    },
  } as unknown as ApolloClient.MutateOptions<TData, TVariables>;

  if (options?.variables) {
    Object.assign(mutationOptions, { variables: options.variables });
  }

  return client.mutate<TData, TVariables>(mutationOptions);
}

class ApolloBaseClient {
  constructor(private readonly _client: ApolloClient) {}

  get client() {
    return this._client;
  }

  async query<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
    query: DocumentNode | TypedDocumentNode<TData, TVariables>,
    variables: TVariables,
    options?: {
      fetchPolicy?: FetchPolicy;
      signal?: AbortSignal;
    }
  ) {
    return await this._client.query({
      query,
      variables,
      fetchPolicy: options?.fetchPolicy ?? EnumFetchPolicy.NETWORK_ONLY,
      context: {
        fetchOptions: buildFetchOptions(options?.signal),
      },
    });
  }

  watchQuery<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
    query: DocumentNode | TypedDocumentNode<TData, TVariables>,
    variables: TVariables,
    options?: {
      fetchPolicy?: FetchPolicy;
      nextFetchPolicy?: FetchPolicy;
      signal?: AbortSignal;
    }
  ) {
    return this._client.watchQuery({
      query,
      variables,
      fetchPolicy: options?.fetchPolicy ?? EnumFetchPolicy.NETWORK_ONLY,
      nextFetchPolicy: options?.fetchPolicy ?? EnumFetchPolicy.NETWORK_ONLY,
      context: {
        fetchOptions: buildFetchOptions(options?.signal),
      },
    });
  }

  async mutation<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
    mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
    variables: TVariables,
    options?: {
      fetchPolicy?: MutationFetchPolicy;
      signal?: AbortSignal;
    }
  ) {
    return await this._client.mutate({
      mutation,
      variables,
      fetchPolicy: options?.fetchPolicy ?? EnumFetchPolicy.NETWORK_ONLY,
      context: {
        fetchOptions: buildFetchOptions(options?.signal),
      },
    });
  }
}

// class ApolloAccountClient extends ApolloBaseClient {
//   constructor() {
//     super(apolloClients[SERVICES.ACCOUNT_SERVICE]);
//   }
// }
//
// class ApolloFaqClient extends ApolloBaseClient {
//   constructor() {
//     super(apolloClients[SERVICES.FAQ_SERVICE]);
//   }
// }

export const apolloAccountClient = new ApolloBaseClient(apolloClients[SERVICES.ACCOUNT_SERVICE]);
export const apolloAccessControlClient = new ApolloBaseClient(apolloClients[SERVICES.ACCESS_CONTROL_SERVICE]);
export const apolloFaqClient = new ApolloBaseClient(apolloClients[SERVICES.FAQ_SERVICE]);
export const apolloInvoiceClient = new ApolloBaseClient(apolloClients[SERVICES.O24_INVOICES_SERVICE]);
export const apolloSalePointClient = new ApolloBaseClient(apolloClients[SERVICES.OB_SALE_POINT]);

export const clearApolloCacheByClient = (clientIds: SERVICES | SERVICES[]) => {
  const ids = Array.isArray(clientIds) ? clientIds : [clientIds];
  return ids.map((clientId) => apolloClients[clientId].clearStore());
};

export const clearAllApolloCaches = () => Object.values(apolloClients).map((client) => client.clearStore());
